import React, { useState } from "react";
import { motion } from "framer-motion";
import { LEARN } from "../../theme";
import { COUNTING_ENGINE_THEME, countingNumberPathValues, countingSpokenWord, countingTarget } from "../../../../data/countingLadderCatalog";
import { ConfettiBurst } from "../../feedback/Celebration";
import {
  BigCounter, CheckIcon, CornerPill, CountingPanel, CountingPiece, CountingRoot, NumberCard, PanelChip, POP_SPRING, ShakeWrap, hexAlpha,
  useCompleteOnce, useCountingDrag, useWrongFlash,
} from "../CountingPiece";

const stoneId = (i) => `stone-${i}`;

/**
 * Hop along the number path: tap the next stone in order and the rabbit
 * hops onto it, saying the number. In drag mode the child carries the
 * target number card to its stone on the path instead.
 */
const NumberPathGame = ({ activity, speak, sfx, onComplete, onWrong, reduceMotion = false }) => {
  const values = countingNumberPathValues(activity);
  const target = countingTarget(activity);
  const drag = activity.interaction === "drag";
  const accent = COUNTING_ENGINE_THEME.numberPath.accent;
  const [step, setStep] = useState(0);
  const [placedAt, setPlacedAt] = useState(null);
  const [tokenSelected, setTokenSelected] = useState(false);
  const [burst, setBurst] = useState(0);
  const complete = useCompleteOnce(onComplete);
  const { flash, isWrong } = useWrongFlash();
  const done = drag ? placedAt != null : step >= values.length;

  const finish = () => {
    setTimeout(() => sfx?.("correct"), 140);
    setBurst((b) => b + 1);
    complete();
  };

  const hop = (index) => {
    if (done) return;
    if (index !== step) {
      flash(index);
      onWrong?.();
      return;
    }
    const next = step + 1;
    setStep(next);
    speak?.(countingSpokenWord(values[index]), { interrupt: true });
    if (next === values.length) finish();
    else sfx?.("pop", { rate: 1 + index * 0.03 });
  };

  const placeToken = (index) => {
    if (done) return false;
    if (values[index] !== target) {
      flash(index);
      onWrong?.();
      return false;
    }
    setPlacedAt(index);
    setTokenSelected(false);
    sfx?.("snap");
    speak?.(countingSpokenWord(target), { interrupt: true });
    finish();
    return true;
  };

  const { dragProps, registerTarget, hoverId, guardTap, GhostLayer } = useCountingDrag({
    reduceMotion,
    sfx,
    onDrop: ({ targetId, targetData }) => (targetId ? placeToken(targetData.index) : false),
  });

  const ghost = (value) => (
    <NumberCard as="div" accent={accent} state="selected" wrapStyle={{ width: "100%", height: "100%" }} style={{ width: "100%", height: "100%", minWidth: 0, minHeight: 0 }}>
      {value}
    </NumberCard>
  );

  const hopperIndex = drag ? placedAt : Math.min(step, values.length - 1);
  const dp = drag ? dragProps(target, ghost) : null;
  const lastValue = step > 0 ? values[step - 1] : 0;

  return (
    <CountingRoot>
      <CountingPanel
        grow
        tint={accent}
        chip={drag ? <PanelChip lead="Find" value={target} accent={accent} /> : undefined}
        corner={drag ? undefined : <CornerPill>{Math.min(step, values.length)} of {values.length}</CornerPill>}
      >
        <div
          style={{
            display: "grid", width: "100%",
            gridTemplateColumns: "repeat(auto-fit, minmax(calc(var(--tile) * 1.1), 1fr))",
            gap: "clamp(6px, 1.5vmin, 12px) clamp(4px, 1vmin, 8px)", alignContent: "center", paddingTop: "calc(var(--tile) * 0.3)",
          }}
        >
          {values.map((value, index) => {
            const current = !drag && index === step && !done;
            const passed = !drag && index < step;
            const hover = hoverId === stoneId(index);
            const seat = drag && placedAt === index;
            // Alternate stones sit a little lower so the row reads as a winding path.
            const dip = index % 2 === 1 ? "calc(var(--tile) * 0.16)" : "0px";
            return (
              <ShakeWrap key={`${value}-${index}`} shake={isWrong(index)} reduceMotion={reduceMotion} style={{ display: "flex", minWidth: 0, paddingTop: dip }}>
                <motion.button
                  type="button"
                  ref={drag ? (el) => registerTarget(stoneId(index), el, { index }) : undefined}
                  className="counting-btn counting-target"
                  aria-label={`Number ${value}`}
                  onClick={guardTap(() => {
                    if (drag) { if (tokenSelected) placeToken(index); return; }
                    hop(index);
                  })}
                  disabled={done}
                  initial={reduceMotion ? false : { scale: 0, opacity: 0 }}
                  animate={{ scale: hover ? 1.1 : current ? 1.04 : 1, opacity: 1 }}
                  transition={{ ...POP_SPRING, delay: reduceMotion ? 0 : Math.min(index * 0.04, 0.5) }}
                  whileTap={reduceMotion || done ? undefined : { scale: 0.94 }}
                  whileHover={reduceMotion || done || passed ? undefined : { scale: 1.06 }}
                  style={{
                    position: "relative", flex: 1, minWidth: 0, aspectRatio: "1 / 1", padding: 0, border: "none", background: "none", "--target-accent": accent,
                    cursor: done ? "default" : "pointer", display: "grid", placeItems: "center", touchAction: "manipulation", borderRadius: 999,
                    outline: current || hover ? `4px solid ${accent}` : "4px solid transparent", outlineOffset: -2, transition: "outline-color 140ms",
                    boxShadow: current || hover ? `0 0 0 8px ${hexAlpha(accent, 0.18)}` : "none",
                  }}
                >
                  <CountingPiece asset="step" variant="bare" size="100%" reduceMotion={reduceMotion} style={{ position: "absolute", inset: 0, filter: passed ? "saturate(0.5) brightness(1.05)" : "none" }} />
                  <span
                    style={{
                      position: "relative", zIndex: 1, fontWeight: 700, fontSize: "clamp(18px, calc(var(--tile) * 0.34), 30px)", lineHeight: 1,
                      color: LEARN.color.ink, background: hexAlpha(LEARN.color.white, 0.92), borderRadius: 999, minWidth: "1.6em", padding: "0.25em 0.4em",
                      boxShadow: `0 calc(var(--tile) * 0.04) 0 ${hexAlpha(LEARN.color.ink, 0.14)}, ${LEARN.shadow.card}`, fontVariantNumeric: "tabular-nums",
                    }}
                  >
                    {value}
                  </span>
                  {passed && (
                    <span style={{ position: "absolute", top: "2%", right: "2%", width: "calc(var(--tile) * 0.3)", height: "calc(var(--tile) * 0.3)", minWidth: 20, minHeight: 20, borderRadius: 999, background: LEARN.color.mint, display: "grid", placeItems: "center", zIndex: 2, border: "2px solid #fff", boxShadow: `0 2px 0 ${LEARN.color.mintDeep}` }}>
                      <CheckIcon size={12} />
                    </span>
                  )}
                  {seat && (
                    <motion.span initial={reduceMotion ? false : { scale: 0.5 }} animate={{ scale: 1 }} transition={POP_SPRING} style={{ position: "absolute", inset: "12%", zIndex: 2, display: "inline-flex" }}>
                      <NumberCard as="div" accent={accent} state="correct" wrapStyle={{ width: "100%", height: "100%" }} style={{ width: "100%", height: "100%", minWidth: 0, minHeight: 0, fontSize: "clamp(18px, calc(var(--tile) * 0.34), 30px)" }}>
                        {target}
                      </NumberCard>
                    </motion.span>
                  )}
                  {hopperIndex === index && (
                    <motion.span
                      layoutId="number-path-hopper"
                      aria-hidden="true"
                      transition={reduceMotion ? { duration: 0 } : { type: "spring", stiffness: 300, damping: 22 }}
                      style={{ position: "absolute", left: "50%", marginLeft: "-36%", top: "-24%", width: "72%", aspectRatio: "1 / 1", zIndex: 3, pointerEvents: "none", display: "inline-flex" }}
                    >
                      <CountingPiece asset="rabbit" variant="bare" size="100%" floats={!done} lifted reduceMotion={reduceMotion} />
                    </motion.span>
                  )}
                </motion.button>
              </ShakeWrap>
            );
          })}
        </div>
        {burst > 0 && <ConfettiBurst key={`burst-${burst}`} count={28} originY={40} reduceMotion={reduceMotion} />}
      </CountingPanel>

      {drag ? (
        <div style={{ display: "flex", justifyContent: "center", minHeight: "calc(var(--tile) * 1.4)", alignItems: "center" }}>
          {!done && (
            <motion.div animate={{ y: tokenSelected ? -8 : 0 }} style={{ display: "inline-flex" }}>
              <NumberCard
                {...dp}
                accent={accent}
                reduceMotion={reduceMotion}
                state={tokenSelected ? "selected" : "idle"}
                onClick={guardTap(() => { sfx?.("pop"); setTokenSelected((s) => !s); })}
                style={{ ...dp.style, cursor: "grab" }}
              >
                {target}
              </NumberCard>
            </motion.div>
          )}
        </div>
      ) : (
        <BigCounter value={lastValue} word={step > 0 ? countingSpokenWord(lastValue) : undefined} accent={accent} placeholder="Hop to the first stone" />
      )}
      <GhostLayer />
    </CountingRoot>
  );
};

export default NumberPathGame;
